import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconBack } from '../components/Icon';
import { setHidden, useAllMedia, useMediaPolling } from '../lib/media';
import type { MediaItem } from '../lib/media';
import { useSession } from '../lib/session';
import { useOnline } from '../lib/online';

/**
 * Artboards 1n (before fullscreen) and 1m (running) — the reception slideshow.
 * These are one page in two states, not two pages: the setup panel is the only
 * chrome that exists, and once Start is pressed there is nothing else.
 *
 * The uncuttable one (D29). Nobody will be watching it to fix problems, so the
 * rules here are about what it must never do:
 *   · never put an error on the wall — a failed refresh keeps cycling what is
 *     already in memory, silently;
 *   · never show a blank screen — an empty manifest gets a holding card;
 *   · never autoplay a video, because videos are not in it at all (D26).
 */

const SLIDE_MS = 9000;
const NO_REPEAT_WINDOW = 0.6;

function shuffle<T>(input: T[]): T[] {
  const arr = [...input];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

export default function Display() {
  const navigate = useNavigate();
  // The whole gallery, not the first page. The deck is a shuffle of everything
  // that has been uploaded, so a partial pool is not a shorter show — it is
  // photographs that never go up on the screen at all, and nobody in the room
  // can tell that apart from the shuffle not having reached theirs yet.
  const media = useAllMedia();
  // The projector's whole job is showing what just happened, so it looks more
  // often than the gallery does. D29: this screen must never stop.
  useMediaPolling(15_000);
  const session = useSession();
  const online = useOnline();
  const [started, setStarted] = useState(false);
  const [index, setIndex] = useState(0);
  const [wakeHint, setWakeHint] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const shownRef = useRef<Set<string>>(new Set());
  const rootRef = useRef<HTMLDivElement>(null);

  // Photos only, and never a hidden one — the room sees what the room sees.
  const pool = useMemo(
    () => media.filter((it) => it.kind === 'photo' && !it.hidden && it.state === 'done'),
    [media],
  );

  const [order, setOrder] = useState<MediaItem[]>([]);

  useEffect(() => {
    setOrder((prev) => {
      // Keep the deck we are already playing; fold new arrivals in rather than
      // reshuffling under the room's feet.
      const known = new Set(prev.map((it) => it.id));
      const added = pool.filter((it) => !known.has(it.id));
      const kept = prev.filter((it) => pool.some((p) => p.id === it.id));
      if (kept.length === 0) return shuffle(pool);
      return added.length ? [...kept, ...shuffle(added)] : kept;
    });
  }, [pool]);

  const advance = useCallback(() => {
    setIndex((i) => {
      if (order.length === 0) return 0;
      const next = i + 1;
      if (next < order.length) return next;
      // Reshuffle at the end, but keep the tail out of the front so the room
      // does not see the same photo twice in a row.
      const recent = new Set(order.slice(Math.floor(order.length * NO_REPEAT_WINDOW)).map((it) => it.id));
      shownRef.current = recent;
      setOrder((prevOrder) => {
        const reshuffled = shuffle(prevOrder);
        const head = reshuffled.filter((it) => !recent.has(it.id));
        const tail = reshuffled.filter((it) => recent.has(it.id));
        return [...head, ...tail];
      });
      return 0;
    });
  }, [order]);

  useEffect(() => {
    if (!started || order.length === 0) return;
    const id = setInterval(advance, SLIDE_MS);
    return () => clearInterval(id);
  }, [started, advance, order.length]);

  // Screen Wake Lock, requested once. If it is refused the operator is told
  // before fullscreen, and never after.
  useEffect(() => {
    let lock: WakeLockSentinel | null = null;
    const nav = navigator as Navigator & { wakeLock?: { request(type: 'screen'): Promise<WakeLockSentinel> } };
    nav.wakeLock?.request('screen').then((l) => { lock = l; }).catch(() => setWakeHint(true));
    if (!nav.wakeLock) setWakeHint(true);
    return () => { void lock?.release(); };
  }, []);

  const current = order[index];

  /**
   * Back to the setup panel.
   *
   * There was no way out at all: Start went fullscreen with `cursor: none` and
   * nothing on screen to click, and pressing Escape dropped the browser out of
   * fullscreen while the app carried on believing it was still running — a
   * windowed slideshow with an invisible cursor and no exit.
   */
  const exit = useCallback(async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
    } catch {
      /* Already out, or never in. Either way `started` is what matters. */
    }
    setStarted(false);
  }, []);

  // The hide hotkey sits exactly where a problem becomes visible — with the
  // person running the screen. It needs an admin session in this browser; the
  // display URL alone cannot change what the room sees.
  useEffect(() => {
    if (!started) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { void exit(); return; }
      if (e.key.toLowerCase() !== 'h' || !current) return;
      if (!session?.isAdmin) {
        setToast('Sign in as an organiser on this laptop to hide anything.');
        setTimeout(() => setToast(null), 4000);
        return;
      }
      setHidden(current.id, true);
      setToast('Hidden. It can be put back from the gallery.');
      setTimeout(() => setToast(null), 3000);
      advance();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [started, current, session, advance, exit]);

  // Escape is what anyone will press first, and the browser eats it to leave
  // fullscreen without telling the app. This is the other half of that.
  useEffect(() => {
    const onChange = () => { if (!document.fullscreenElement) setStarted(false); };
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  async function start() {
    try {
      await rootRef.current?.requestFullscreen?.();
    } catch {
      /* Fullscreen is a nicety. If the browser refuses it, the show still runs. */
    }
    setStarted(true);
  }

  return (
    <div className="kiosk" ref={rootRef} style={{ cursor: started ? 'none' : 'auto' }}>
      {order.length === 0 ? (
        <div className="kiosk-hold">
          <div>
            <div className="brand">M&amp;V</div>
            <p style={{ color: '#dcd3c4', fontSize: 18, marginTop: 12 }}>
              Nothing has been uploaded yet. The first photo of the night lands here.
            </p>
          </div>
        </div>
      ) : (
        order.map((item, i) => (
          <div key={item.id} className={`kiosk-slide${i === index ? ' on' : ''}`} aria-hidden={i !== index}>
            {/* Blurred backdrop, then the photo whole on top of it. A portrait
                shot is never cropped to fit a landscape screen. */}
            <img className="fill" src={item.src} alt="" aria-hidden="true" />
            <img className="shot" src={item.src} alt="" />
          </div>
        ))
      )}


      <div className="kiosk-mono">M&amp;V</div>

      {/* D38: translucent grey band, white centred sans-serif — and it fades
          away entirely when a photo has no caption rather than sitting empty. */}
      <div className={`kiosk-band${current?.caption ? ' on' : ''}`}>
        <div className="cap">{current?.caption}</div>
        <div className="by">{current?.uploaderName}</div>
      </div>

      {toast && <div className="kiosk-toast">{toast}</div>}

      {!started && (
        <div className="kiosk-setup">
          <div className="kiosk-preview">
            {current ? <img src={current.src} alt="" /> : (
              /* Nothing to preview yet. Say so — a generated abstract here
                 made the screen look loaded when the pool was empty, which is
                 the one thing the operator needs to know before starting. */
              <div className="kiosk-preview-hold">
                <div className="brand">M&amp;V</div>
                <p>No photos yet. The first upload of the night appears here.</p>
              </div>
            )}
            {current?.caption && (
              <div className="kiosk-band on" style={{ padding: '22px 34px' }}>
                <div className="cap" style={{ fontSize: 24 }}>{current.caption}</div>
              </div>
            )}
          </div>
          <div className="kiosk-bar">
            <button
              type="button" className="kiosk-back" onClick={() => navigate('/now')}
              aria-label="Back to the app"
            >
              <IconBack size={16} /> Back
            </button>
            <div className="brand">M&amp;V</div>
            <div className="facts">
              <span>{pool.length} {pool.length === 1 ? 'photo' : 'photos'} ready</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <span className={`kiosk-dot${online ? '' : ' off'}`} />
                {online ? 'Connected' : 'Offline — it will keep playing what it has'}
              </span>
              <span>Press <strong>H</strong> to hide whatever is on screen</span>
              {/* Escape is the only way out once this starts and there is
                  deliberately nothing on screen to click, so the one place it
                  can be said is here, before it starts. */}
              <span>Press <strong>Esc</strong> to come back here</span>
              {!session?.isAdmin && <span style={{ color: '#f6a06b' }}>Sign in as an organiser first to use it</span>}
              {wakeHint && <span style={{ color: '#f6a06b' }}>This browser won't hold the screen awake — turn off sleep in system settings</span>}
            </div>
            <button type="button" className="btn btn-primary" style={{ minHeight: 48, paddingInline: 26, fontSize: 16 }} onClick={start}>
              Start the slideshow
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
